<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { staticPageHref, accountHref } from '@/router/products'
import { useAuth } from '@/composables/useAuth'
import { updatePassword } from '@/services/account'
import { ApiError } from '@/services/http'
import { usePasswordRules } from '@/composables/usePasswordRules'
import AccountLayout from '@/components/layout/AccountLayout.vue'
import PasswordRulesChecklist from '@/components/ui/PasswordRulesChecklist.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { member } = useAuth()

const currentLocale = computed<Locale>(() => (route.meta.locale as Locale) ?? DEFAULT_LOCALE)
const loginHref = computed(() => staticPageHref('login', currentLocale.value))
const contactHref = computed(() => accountHref('contact', currentLocale.value))
const dashboardHref = computed(() => accountHref('dashboard', currentLocale.value))
useHead({ title: computed(() => `${t('auth.account.credentials.navTitle')} — MySmerra`) })

/** Accès réservé + ouverture directe du formulaire depuis le dashboard
 *  (lien « Modifier le mot de passe » → ?edit=password). */
onMounted(() => {
  if (!member.value) {
    router.replace(loginHref.value)
    return
  }
  if (route.query.edit === 'password') startChange()
})

/* ============================================================================
 *  Modification du mot de passe — utilisateur connecté : vérification du mot de
 *  passe actuel puis mise à jour, en un seul appel (PATCH /users), sans email.
 * ========================================================================== */
type Step = 'idle' | 'form' | 'done'
const step = ref<Step>('idle')
const isLoading = ref(false)

const currentPassword = ref('')
const password = ref('')
const confirm = ref('')
const showCurrent = ref(false)
const showPassword = ref(false)
const showConfirm = ref(false)

const currentPasswordError = ref<string | null>(null)
const passwordError = ref<string | null>(null)
const generalError = ref<string | null>(null)

// Règles de complexité mutualisées (cf. usePasswordRules).
const { checklist, canSubmit } = usePasswordRules(password, confirm)
/** Actif uniquement si le mot de passe actuel est saisi ET le nouveau est valide. */
const canSubmitForm = computed(() => currentPassword.value.length > 0 && canSubmit.value)

function reset() {
  step.value = 'idle'
  currentPassword.value = ''
  password.value = ''
  confirm.value = ''
  showCurrent.value = false
  showPassword.value = false
  showConfirm.value = false
  currentPasswordError.value = null
  passwordError.value = null
  generalError.value = null
}

/** Ouvre le formulaire de changement de mot de passe. */
function startChange() {
  reset()
  step.value = 'form'
}

/** Vrai quand un 422 porte sur le mot de passe actuel (mauvais mot de passe). */
function isWrongCurrentPassword(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) return false
  const errors = (body as { errors?: unknown }).errors
  return typeof errors === 'object' && errors !== null && 'current_password' in errors
}

/** Vérifie le mot de passe actuel et applique le nouveau (PATCH /users). */
async function submit() {
  currentPasswordError.value = null
  passwordError.value = null
  generalError.value = null
  if (!currentPassword.value) {
    currentPasswordError.value = t('auth.account.credentials.changePassword.errCurrentRequired')
    return
  }
  if (!canSubmit.value) return
  isLoading.value = true
  try {
    await updatePassword(currentPassword.value, password.value, confirm.value)
    step.value = 'done'
  } catch (err) {
    if (err instanceof ApiError && err.status === 422) {
      if (isWrongCurrentPassword(err.body)) {
        currentPasswordError.value = t('auth.account.credentials.changePassword.errCurrentWrong')
      } else {
        passwordError.value = t('auth.account.credentials.changePassword.errInvalid')
      }
    } else {
      generalError.value = t('auth.account.credentials.changePassword.errNetwork')
    }
  } finally {
    isLoading.value = false
  }
}

/** Fin du parcours : retour à l'accueil de l'espace connecté (session conservée). */
function backHome() {
  reset()
  router.push(dashboardHref.value)
}
</script>

<template>
  <AccountLayout v-if="member" active="credentials">
    <section class="cred">
      <!-- Fil d'ariane + titre -->
      <nav class="cred__crumb" aria-label="breadcrumb">
        <span>{{ t('auth.account.credentials.breadcrumbHome') }}</span>
        <span class="cred__crumb-sep" aria-hidden="true">›</span>
        <span class="cred__crumb-current">{{ t('auth.account.credentials.navTitle') }}</span>
      </nav>
      <header class="cred__head">
        <h1 class="cred__title">{{ t('auth.account.credentials.navTitle') }}</h1>
        <p class="cred__subtitle">{{ t('auth.account.credentials.subtitle') }}</p>
      </header>

      <div class="cred__grid">
        <!-- Colonne principale -->
        <div class="cred__main">
          <!-- Email -->
          <div class="field">
            <span class="field__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <rect x="3" y="5" width="18" height="14" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.6"/>
                <path d="M4 7l8 6 8-6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <div class="field__text">
              <span class="field__label">{{ t('auth.account.credentials.email') }}</span>
              <span class="field__value">{{ member.email }}</span>
            </div>
          </div>

          <!-- Mot de passe : affichage statique ou flux de modification -->
          <div v-if="step === 'idle'" class="field">
            <span class="field__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <rect x="4.5" y="10" width="15" height="10" rx="2" fill="none" stroke="currentColor" stroke-width="1.6"/>
                <path d="M8 10V7.5a4 4 0 0 1 8 0V10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                <circle cx="12" cy="15" r="1.3" fill="currentColor"/>
              </svg>
            </span>
            <div class="field__text">
              <span class="field__label">{{ t('auth.account.credentials.password') }}</span>
              <span class="field__value field__value--dots">••••••••</span>
            </div>
            <button type="button" class="btn-dark field__cta" @click="startChange">{{ t('auth.account.credentials.editPassword') }}</button>
          </div>

          <div v-else class="flow">
            <h2 class="flow__title">{{ t('auth.account.credentials.password') }}</h2>

            <!-- Mot de passe actuel + nouveau mot de passe -->
            <form v-if="step === 'form'" class="flow__step" novalidate @submit.prevent="submit">
              <label class="flow__label" for="cp-current">{{ t('auth.account.credentials.changePassword.currentLabel') }}</label>
              <div class="flow__field">
                <input id="cp-current" v-model="currentPassword" class="flow__input" :class="{ 'is-invalid': currentPasswordError }" :type="showCurrent ? 'text' : 'password'" autocomplete="current-password" @input="currentPasswordError = null" />
                <button type="button" class="flow__eye" :aria-label="showCurrent ? t('auth.account.credentials.changePassword.hidePassword') : t('auth.account.credentials.changePassword.showPassword')" :aria-pressed="showCurrent" @click="showCurrent = !showCurrent">
                  <svg v-if="showCurrent" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
                    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.8"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path d="M2 12s3.5-7 10-7c2 0 3.8.66 5.3 1.6M22 12s-3.5 7-10 7c-2 0-3.8-.66-5.3-1.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M4 4l16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <p v-if="currentPasswordError" class="flow__error">{{ currentPasswordError }}</p>

              <label class="flow__label" for="cp-pw">{{ t('auth.account.credentials.changePassword.passwordLabel') }}</label>
              <div class="flow__field">
                <input id="cp-pw" v-model="password" class="flow__input" :class="{ 'is-invalid': passwordError }" :type="showPassword ? 'text' : 'password'" autocomplete="new-password" />
                <button type="button" class="flow__eye" :aria-label="showPassword ? t('auth.account.credentials.changePassword.hidePassword') : t('auth.account.credentials.changePassword.showPassword')" :aria-pressed="showPassword" @click="showPassword = !showPassword">
                  <svg v-if="showPassword" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
                    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.8"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path d="M2 12s3.5-7 10-7c2 0 3.8.66 5.3 1.6M22 12s-3.5 7-10 7c-2 0-3.8-.66-5.3-1.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M4 4l16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>

              <label class="flow__label" for="cp-confirm">{{ t('auth.account.credentials.changePassword.confirmLabel') }}</label>
              <div class="flow__field">
                <input id="cp-confirm" v-model="confirm" class="flow__input" :class="{ 'is-invalid': passwordError }" :type="showConfirm ? 'text' : 'password'" autocomplete="new-password" />
                <button type="button" class="flow__eye" :aria-label="showConfirm ? t('auth.account.credentials.changePassword.hidePassword') : t('auth.account.credentials.changePassword.showPassword')" :aria-pressed="showConfirm" @click="showConfirm = !showConfirm">
                  <svg v-if="showConfirm" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
                    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.8"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path d="M2 12s3.5-7 10-7c2 0 3.8.66 5.3 1.6M22 12s-3.5 7-10 7c-2 0-3.8-.66-5.3-1.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M4 4l16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <PasswordRulesChecklist :checklist="checklist" />

              <p v-if="passwordError" class="flow__error">{{ passwordError }}</p>
              <p v-else-if="generalError" class="flow__error">{{ generalError }}</p>

              <div class="flow__actions">
                <button type="submit" class="btn-dark" :disabled="isLoading || !canSubmitForm">{{ t('auth.account.credentials.changePassword.submit') }}</button>
                <button type="button" class="btn-ghost" @click="reset">{{ t('auth.account.credentials.changePassword.cancel') }}</button>
              </div>
            </form>

            <!-- Étape finale : confirmation -->
            <div v-else class="success">
              <span class="success__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="26" height="26">
                  <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.7"/>
                  <path d="M8 12.5l2.5 2.5L16 9.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <h3 class="success__title">{{ t('auth.account.credentials.changePassword.successTitle') }}</h3>
              <p class="success__desc">{{ t('auth.account.credentials.changePassword.success') }}</p>
              <button type="button" class="btn-dark" @click="backHome">{{ t('auth.account.credentials.changePassword.backHome') }}</button>
            </div>
          </div>

          <!-- Conseil de sécurité -->
          <div v-if="step === 'idle'" class="security">
            <span class="security__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                <path d="M9 12l2 2 4-4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <div class="security__body">
              <p class="security__title">{{ t('auth.account.credentials.securityTitle') }}</p>
              <p class="security__desc">{{ t('auth.account.credentials.securityDesc') }}</p>
            </div>
          </div>
        </div>

        <!-- Colonne latérale -->
        <aside class="cred__aside">
          <article class="aside-card">
            <span class="aside-card__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
                <path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.7.4-1.1.9-1.1 1.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                <circle cx="12" cy="16.5" r="1" fill="currentColor"/>
              </svg>
            </span>
            <h2 class="aside-card__title">{{ t('auth.account.credentials.helpTitle') }}</h2>
            <p class="aside-card__desc">{{ t('auth.account.credentials.helpDesc') }}</p>
            <RouterLink class="btn-dark aside-card__cta" :to="contactHref">{{ t('auth.account.credentials.helpCta') }}</RouterLink>
          </article>
        </aside>
      </div>
    </section>
  </AccountLayout>
</template>

<style scoped>
.cred { display: flex; flex-direction: column; gap: 1rem; }

/* Fil d'ariane + titre */
.cred__crumb { display: flex; align-items: center; gap: 0.4rem; font-size: var(--text-sm); color: var(--text-muted); }
.cred__crumb-current { color: var(--color-chocolate); font-weight: 600; }
.cred__title { font-size: var(--text-xl); font-weight: 700; color: var(--color-chocolate); }
.cred__subtitle { margin-top: 0.15rem; font-size: var(--text-sm); color: var(--text-muted); }

/* Grille principale / latérale */
.cred__grid {
  display: grid;
  grid-template-columns: 1.7fr 1fr;
  gap: 1.25rem;
  align-items: start;
  margin-top: 0.25rem;
}
.cred__main {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}
.cred__aside { display: flex; flex-direction: column; gap: 1.25rem; }

/* Champ (email / mot de passe) */
.field {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  column-gap: 0.9rem;
  row-gap: 0.6rem;
  padding: 0.9rem 0.25rem;
  border-bottom: 1px solid var(--border);
}
.field__icon {
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: var(--radius-md);
  color: var(--primary);
  background: color-mix(in oklab, var(--primary) 10%, white);
}
.field__text { display: flex; flex-direction: column; gap: 0.15rem; min-width: 0; }
.field__label { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.field__value { font-size: var(--text-sm); color: var(--text-muted); word-break: break-all; }
.field__value--dots { letter-spacing: 0.15em; }
.field__cta { text-decoration: none; }
@media (max-width: 40rem) {
  .field { grid-template-columns: auto 1fr; }
  .field__cta { grid-column: 1 / -1; justify-self: start; }
}

/* Flux de modification du mot de passe */
.flow {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin: 0.4rem 0;
  padding: 1.1rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg-soft);
}
.flow__title { font-size: var(--text-base); font-weight: 700; color: var(--color-chocolate); }
.flow__step { display: flex; flex-direction: column; gap: 0.6rem; }
.flow__intro { font-size: var(--text-sm); color: var(--text-muted); line-height: var(--leading-normal); }
.flow__label { font-size: var(--text-xs); font-weight: 600; color: var(--color-chocolate); }
.flow__field { position: relative; }
.flow__input {
  width: 100%;
  padding: 0.65rem 0.8rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  background: var(--color-white);
  font: inherit;
  font-size: var(--text-sm);
  color: var(--color-chocolate);
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.flow__field .flow__input { padding-right: 2.75rem; }
.flow__input:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--focus-ring); }
.flow__input.is-invalid { border-color: var(--color-cherry); box-shadow: 0 0 0 3px color-mix(in oklab, var(--color-cherry) 22%, transparent); }
.flow__input--locked { background: var(--color-gray-100); color: var(--text-muted); cursor: not-allowed; }
.flow__eye {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  padding: 0.25rem;
  border: 0;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
}
.flow__eye:hover { color: var(--color-chocolate); }
.flow__error { font-size: var(--text-xs); font-weight: 600; color: var(--color-cherry); }
.flow__actions { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 0.2rem; }

/* Saisie du code (OTP) */
.otp { display: flex; gap: 0.6rem; }
.otp__input {
  width: 3rem;
  height: 3.25rem;
  text-align: center;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  background: var(--color-white);
  font: inherit;
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--color-chocolate);
}
.otp__input:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--focus-ring); }
.otp__input.is-invalid { border-color: var(--color-cherry); }

/* Règles du mot de passe */
.rules { display: flex; flex-direction: column; gap: 0.4rem; }
.rules__title { font-size: var(--text-xs); font-weight: 600; color: var(--color-chocolate); }
.rules__list { display: flex; flex-direction: column; gap: 0.25rem; margin: 0; padding: 0; list-style: none; }
.rule { display: flex; align-items: center; gap: 0.45rem; font-size: var(--text-xs); color: var(--text-muted); }
.rule__icon { flex-shrink: 0; color: var(--border-strong); }
.rule.is-ok { color: #1a7f45; }
.rule.is-ok .rule__icon { color: #1a7f45; }

/* Confirmation finale */
.success {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
}
.success__icon { color: #1a7f45; }
.success__title { font-size: var(--text-base); font-weight: 700; color: var(--color-chocolate); }
.success__desc { font-size: var(--text-sm); color: var(--text-muted); line-height: var(--leading-normal); }
.success .btn-dark { margin-top: 0.3rem; }

/* Conseil de sécurité */
.security {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  margin-top: 0.6rem;
  padding: 0.9rem 1rem;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg-soft);
}
.security__icon { color: var(--primary); flex-shrink: 0; margin-top: 0.1rem; }
.security__title { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.security__desc { margin-top: 0.1rem; font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }

/* Carte latérale */
.aside-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}
.aside-card__icon {
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--radius-lg);
  color: var(--primary);
  background: color-mix(in oklab, var(--primary) 12%, white);
}
.aside-card__title { font-size: var(--text-base); font-weight: 700; color: var(--color-chocolate); }
.aside-card__desc { font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }
.aside-card__cta { margin-top: 0.2rem; text-decoration: none; }

/* Boutons */
.btn-dark {
  padding: 0.7rem 1.4rem;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--color-chocolate);
  color: var(--color-white);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease-out);
}
.btn-dark:hover { background: color-mix(in oklab, var(--color-chocolate) 85%, white); }
.btn-dark:disabled { opacity: 0.5; cursor: default; }
.btn-dark:disabled:hover { background: var(--color-chocolate); }
.btn-ghost {
  padding: 0.7rem 1.4rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);
  background: var(--color-white);
  color: var(--color-chocolate);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out);
}
.btn-ghost:hover { border-color: var(--primary); }

@media (max-width: 64rem) {
  .cred__grid { grid-template-columns: 1fr; }
}
</style>
